import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { getSourceIdByLocale } from '@wkda/funnel-utils';
import { useMileagePriceTag } from '../../booking/hooks';
import { useAppConfiguration } from '../useAppConfiguration';
import useCarleadHashParam from '../useCarleadHashParam';
import useConstant from '../useConstant';
import { useConsumerSourceId } from '../useDataHooks';
import { useGetWKDALeadCookie } from '../useGetWKDALeadCookie';
import { useModulesStore } from '../useModules';
import { getBookingPageUxFeatures } from './helpers';
import { mileagePriceTagTrackingParams } from './mileagePriceTagTracking';

/**
 * Util function returns params depend on application config.
 */
export function getTrackingParamsByConfig(config, consumerSourceId) {
  const {
    appName,
    appVersion,
    sourceId: configSourceId,
    locale
  } = config;
  const sourceId = consumerSourceId || configSourceId || getSourceIdByLocale(locale);
  return {
    appver: `${appName.replace('@wkda/funnels-', '')}@${appVersion}`,
    appname: appName,
    source: sourceId
  };
}
export function useGetMarketingTrackingService() {
  const {
    tracking: {
      marketingTrackingService
    }
  } = useModulesStore();
  return useMemo(() => {
    return marketingTrackingService;
  }, [marketingTrackingService]);
}
function trackLandingWrapper(config, consumerSourceId, carleadHash, params, service, additionalParams) {
  const {
    trackLandingPageType,
    appName,
    env
  } = config;
  if (trackLandingPageType) {
    const configParams = getTrackingParamsByConfig(config, consumerSourceId);
    const trackingPayload = {
      pageType: trackLandingPageType,
      ...configParams,
      ...params
    };

    // booking landing is tracked differently (with external tracking)
    const isBookingLanding = appName.startsWith('@wkda/') && trackLandingPageType === 'booking-handover' && !!carleadHash;
    if (isBookingLanding) {
      // if this is booking page, then we are sure this is the booking configuration
      const bookingConfig = config;
      return service.trackExternal({
        externalType: 'carlead',
        externalId: carleadHash
      }, {
        ...trackingPayload,
        category: bookingConfig.bookingPageType,
        action: 'booking-landing',
        uxFeatures: getBookingPageUxFeatures(window?.location?.href),
        ...mileagePriceTagTrackingParams(additionalParams?.mileagePriceTag)
      });
    } else {
      return service.trackLanding(trackingPayload);
    }
  } else {
    if (env !== 'prod') console.warn('[Warning] - Track landing works only if trackLandingPageType is added to the funnel config');
  }
}
const trackLeadFieldChangedWrapper = _ref => {
  let {
    config,
    consumerSourceId,
    fieldName,
    method,
    service,
    eventData
  } = _ref;
  const configParams = getTrackingParamsByConfig(config, consumerSourceId);
  return service.trackLeadFieldChanged({
    ...configParams,
    leadFieldChanged: fieldName,
    method,
    eventData
  });
};
function trackExternalWrapper(config, consumerSourceId, externalType, externalId, category, action) {
  let additionalData = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : {};
  let service = arguments.length > 7 ? arguments[7] : undefined;
  const configParams = getTrackingParamsByConfig(config, consumerSourceId);
  return service.trackExternal({
    externalType,
    externalId
  }, {
    ...configParams,
    action,
    category,
    ...additionalData
  });
}
export function useTrackLandingCallback(params) {
  const config = useAppConfiguration();
  const sourceId = useConsumerSourceId();
  const service = useGetMarketingTrackingService();
  const carleadHash = useCarleadHashParam();
  const mileagePriceTag = useMileagePriceTag();
  return useCallback(() => {
    return trackLandingWrapper(config, sourceId, carleadHash, params, service, {
      mileagePriceTag
    });
  }, [carleadHash, config, mileagePriceTag, params, service, sourceId]);
}
export function useTrackLanding(params) {
  const config = useAppConfiguration();
  const sourceId = useConsumerSourceId();
  const carleadHash = useCarleadHashParam();
  const configConstant = useConstant(() => config);
  const sourceIdConstant = useConstant(() => sourceId);
  const paramsConstant = useConstant(() => params);
  const carleadHashConstant = useConstant(() => carleadHash);
  const service = useGetMarketingTrackingService();
  const mileagePriceTag = useMileagePriceTag();
  useEffect(() => {
    trackLandingWrapper(configConstant, sourceIdConstant, carleadHashConstant, paramsConstant, service, {
      mileagePriceTag
    });
  }, [configConstant, sourceIdConstant, carleadHashConstant, paramsConstant, service, mileagePriceTag]);
}
export function useTrackLeadFieldChanged() {
  const config = useAppConfiguration();
  const sourceId = useConsumerSourceId();
  const service = useGetMarketingTrackingService();
  return useCallback((fieldName, method, eventData) => {
    return trackLeadFieldChangedWrapper({
      config,
      consumerSourceId: sourceId,
      fieldName,
      method,
      service,
      eventData
    });
  }, [config, service, sourceId]);
}
export function useTrackLeadFormEvent() {
  const config = useAppConfiguration();
  const sourceId = useConsumerSourceId();
  const service = useGetMarketingTrackingService();
  const configParams = getTrackingParamsByConfig(config, sourceId);
  return (action, method) =>
  // @ts-expect-error leadFieldChanged property is required in trackLeadFieldChanged function but not in actual service.
  service.trackLeadFieldChanged({
    ...configParams,
    action,
    method
  });
}
export function useTrackExternal() {
  const config = useAppConfiguration();
  const sourceId = useConsumerSourceId();
  const service = useGetMarketingTrackingService();
  const {
    mutateAsync
  } = useMutation({
    mutationFn: _ref2 => {
      let {
        externalType,
        externalId,
        category,
        action,
        additionalData
      } = _ref2;
      return trackExternalWrapper(config, sourceId, externalType, externalId, category, action, additionalData, service);
    },
    retry: 4,
    retryDelay: attempt => {
      return Math.min(attempt > 1 ? 2 ** attempt * 1000 : 1000, 30 * 1000);
    }
  });
  const trackExternal = useCallback((externalType, externalId, category, action, additionalData) => {
    return mutateAsync({
      externalType,
      externalId,
      category,
      action,
      additionalData
    });
  }, [mutateAsync]);
  return trackExternal;
}
export function useTrackCustomerBanner() {
  const service = useGetMarketingTrackingService();
  const config = useAppConfiguration();
  const configParams = getTrackingParamsByConfig(config);
  const wkdaLead = useGetWKDALeadCookie();
  const CJ_TRACKING_CTA_ATTR = 'data-cj-tracking-button-name';
  function getCJTrackingButtonNameByAttr(element) {
    const ctaName = element.getAttribute(CJ_TRACKING_CTA_ATTR);
    if (!ctaName) {
      console.warn(`[CJ Tracking] No button name found on element by ${CJ_TRACKING_CTA_ATTR} attribute.`);
    }
    return ctaName;
  }
  function trackShownEvent(bannerName) {
    return service.trackCustomerBanner({
      externalType: 'carlead',
      externalId: wkdaLead
    }, {
      ...configParams,
      action: 'customer-banner-shown',
      category: 'customer-banner',
      landingPageName: window.location.pathname,
      bannerName
    });
  }
  function trackCTAClickEvent(bannerName, ctaName) {
    service.trackCustomerBanner({
      externalType: 'carlead',
      externalId: wkdaLead
    }, {
      ...configParams,
      action: 'customer-banner-cta-click',
      category: 'customer-banner',
      landingPageName: window.location.pathname,
      bannerName,
      ctaName
    });
  }
  return {
    trackShownEvent,
    trackCTAClickEvent,
    CJ_TRACKING_CTA_ATTR,
    getCJTrackingButtonNameByAttr
  };
}
export function useTrackEvent() {
  const service = useGetMarketingTrackingService();
  const config = useAppConfiguration();
  const sourceId = useConsumerSourceId();
  return useCallback(params => {
    const baseParams = getTrackingParamsByConfig(config, sourceId);
    return service.trackEvent({
      ...baseParams,
      ...params
    }, {
      includeLandingParams: true
    });
  }, [config, service, sourceId]);
}